import { OnboardingStepStatus } from '@today/types'
import { Button } from '@today/ui-primitives'
import type { OnboardingWizardState } from '@/hooks/useOnboardingWizard'
import { canGoNext, showDoLater } from '@/modules/onboarding/wizard-navigation'

/** ux-interaction-standards §13: every action is a ≥44px touch target (`md` alone is 40px). */
const TOUCH_TARGET = 'min-h-11'

/**
 * The wizard step's actions (TO-190 REQ-010): Back, Mark as complete, Do this
 * later and Next, each gated by the navigation rules. Design-system buttons
 * (TO-733 REQ-007): the pressed write shows its loading state, and both writes
 * are disabled while either is in flight.
 */
export function StepActions({ state }: { state: OnboardingWizardState }) {
	const { active: step, index, onboarding } = state
	const isLast = index === onboarding.steps.length - 1
	return (
		<div className="flex flex-wrap gap-2">
			{index > 0 && (
				<Button variant="secondary" className={TOUCH_TARGET} onPress={() => state.move(-1)}>
					Back
				</Button>
			)}
			{step.status !== OnboardingStepStatus.COMPLETE && (
				<WriteButton variant="primary" writes={OnboardingStepStatus.COMPLETE} state={state} onPress={state.complete}>
					Mark as complete
				</WriteButton>
			)}
			{showDoLater(step) && (
				<WriteButton variant="muted" writes={OnboardingStepStatus.SKIPPED} state={state} onPress={state.doLater}>
					Do this later
				</WriteButton>
			)}
			{!isLast && (
				<Button
					variant="secondary"
					className={TOUCH_TARGET}
					isDisabled={!canGoNext(step)}
					onPress={() => state.move(1)}
				>
					Next
				</Button>
			)}
		</div>
	)
}

/** A step-update action: loading while its own status is in flight, disabled while any update is. */
function WriteButton({
	variant,
	writes,
	state,
	onPress,
	children,
}: {
	variant: 'primary' | 'muted'
	writes: OnboardingStepStatus
	state: Pick<OnboardingWizardState, 'busy' | 'pendingStatus'>
	onPress: () => void
	children: string
}) {
	return (
		<Button
			variant={variant}
			className={TOUCH_TARGET}
			isLoading={state.pendingStatus === writes}
			isDisabled={state.busy}
			onPress={onPress}
		>
			{children}
		</Button>
	)
}
